import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Printer, Plus, Package, CheckCircle, ClipboardList } from 'lucide-react';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import DigitalPrintingAktifSiparislerTab from '../../components/customer-portal/DigitalPrintingAktifSiparislerTab';
import DigitalPrintingTamamlananSiparislerTab from '../../components/customer-portal/DigitalPrintingTamamlananSiparislerTab';
import DigitalPrintingNumuneIstekleriTab from '../../components/customer-portal/DigitalPrintingNumuneIstekleriTab';
import DigitalPrintingCustomerPatternList from '../../components/customer-portal/DigitalPrintingCustomerPatternList';

const CustomerDigitalPrinting = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  // URL'den tab parametresini oku ve aktif sekme olarak ayarla
  const getInitialTab = () => {
    if (tabParam) {
      const tabMap = {
        'create': 'create',
        'aktif': 'aktif',
        'tamamlanan': 'tamamlanan',
        'numune': 'numune'
      };
      return tabMap[tabParam] || 'aktif';
    }
    return 'aktif';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const isUpdatingFromUrlRef = useRef(false);

  const handleTabChange = (tabId) => {
    isUpdatingFromUrlRef.current = false;
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const sections = [
    { id: 'create', label: 'Baskı Siparişi Oluştur', icon: Plus, color: 'blue', count: null },
    { id: 'aktif', label: 'Aktif Siparişler', icon: Package, color: 'blue', count: null },
    { id: 'tamamlanan', label: 'Tamamlanan Siparişler', icon: CheckCircle, color: 'green', count: null },
    { id: 'numune', label: 'Numune İstekleri', icon: ClipboardList, color: 'purple', count: null }
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: {
        bg: isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-blue-600 dark:text-blue-400',
        text: isActive ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white',
        count: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
      },
      green: {
        bg: isActive ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-green-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-green-600 dark:text-green-400',
        text: isActive ? 'text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-white',
        count: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
      },
      purple: {
        bg: isActive ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-purple-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-purple-600 dark:text-purple-400',
        text: isActive ? 'text-purple-900 dark:text-purple-100' : 'text-gray-900 dark:text-white',
        count: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      },
    };
    return colors[color] || colors.blue;
  };

  // URL'den tab parametresini oku ve aktif sekme olarak ayarla
  useEffect(() => {
    if (tabParam) {
      const tabMap = {
        'create': 'create',
        'aktif': 'aktif',
        'tamamlanan': 'tamamlanan',
        'numune': 'numune'
      };
      if (tabMap[tabParam] && tabMap[tabParam] !== activeTab) {
        isUpdatingFromUrlRef.current = true;
        setActiveTab(tabMap[tabParam]);
        setTimeout(() => {
          isUpdatingFromUrlRef.current = false;
        }, 100);
      }
    }
  }, [tabParam, activeTab]);

  return (
    <CustomerPortalLayout title="Dijital Baskı" icon={Printer} showBackButton={true}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Cards - Mobilde 2 sütun, Desktop'ta 4 sütun */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeTab === section.id;
            const colors = getColorClasses(section.color, isActive);
            
            return (
              <button
                key={section.id}
                onClick={() => {
                  if (section.id === 'create') {
                    handleTabChange('create');
                  } else {
                    handleTabChange(section.id);
                  }
                }}
                className={`
                  ${colors.bg} ${colors.border} border-2 rounded-lg px-2 sm:px-3 py-2 sm:py-3 text-left transition-all
                  hover:shadow-md hover:scale-[1.02] active:scale-[0.98]
                  ${isActive ? 'shadow-lg ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800' : ''}
                  flex items-center gap-1.5 sm:gap-2 h-full
                `}
              >
                <div className={`${colors.icon} p-1.5 sm:p-2 rounded-lg bg-white dark:bg-gray-700 flex-shrink-0`}>
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <h3 className={`${colors.text} font-semibold text-xs sm:text-sm leading-tight break-words`}>
                    {section.label}
                  </h3>
                  {section.count !== null && (
                    <div className="mt-1 sm:mt-1.5">
                      {section.count > 0 ? (
                        <span className={`${colors.count} text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full inline-block`}>
                          {section.count}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                          {section.count}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Section Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm sm:shadow-md p-3 sm:p-4 lg:p-6">
          {activeTab === 'create' && (
            <DigitalPrintingCustomerPatternList />
          )}
          
          {activeTab === 'aktif' && (
            <DigitalPrintingAktifSiparislerTab />
          )}
          
          {activeTab === 'tamamlanan' && (
            <DigitalPrintingTamamlananSiparislerTab />
          )}
          
          {activeTab === 'numune' && (
            <DigitalPrintingNumuneIstekleriTab />
          )}
        </div>
      </main>

    </CustomerPortalLayout>
  );
};

export default CustomerDigitalPrinting;
